import { useState } from 'react';
import { Modal } from '@/shared/ui/Modal';
import { Input } from '@/shared/ui/Input';
import { Select } from '@/shared/ui/Select';
import { Button } from '@/shared/ui/Button';
import { Callout } from '@/shared/ui/Callout';
import { useCreatePractice } from '../hooks/useCreatePractice';
import { agencyApi, type CreatePracticePayload } from '../api/agencyApi';
import {
  DEFAULT_VERTICAL,
  VERTICAL_OPTIONS,
  specialtyOptions,
  type VerticalId,
} from '@/shared/constants/verticals';
import {
  validateEmail,
  validatePhone,
  formatPhoneDisplay,
  PHONE_PLACEHOLDER,
} from '@/shared/utils/validation';
import styles from './CreatePracticeModal.module.css';

interface FormData {
  name: string;
  vertical: VerticalId;
  specialty: string;
  website: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  contact: { name: string; email: string; phone: string };
}

const EMPTY_FORM: FormData = {
  name: '',
  vertical: DEFAULT_VERTICAL,
  specialty: '',
  website: '',
  phone: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  contact: { name: '', email: '', phone: '' },
};

interface CreatePracticeModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function CreatePracticeModal({ isOpen, onClose }: CreatePracticeModalProps) {
  const [form, setForm] = useState<FormData>(EMPTY_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [conflict, setConflict] = useState<{
    existingPractices: Array<{ id: string; name: string }>;
  } | null>(null);
  const [checking, setChecking] = useState(false);
  const { mutate, isPending } = useCreatePractice();

  const handleClose = () => {
    setForm(EMPTY_FORM);
    setErrors({});
    setConflict(null);
    onClose();
  };

  const setContact = (field: keyof FormData['contact'], value: string) => {
    setForm((prev) => ({ ...prev, contact: { ...prev.contact, [field]: value } }));
    setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!form.name.trim()) newErrors.name = 'Practice name is required';
    if (!form.contact.name.trim()) newErrors.contactName = 'Contact name is required';
    const emailErr = validateEmail(form.contact.email);
    if (emailErr) newErrors.email = emailErr;
    const phoneErr = validatePhone(form.contact.phone);
    if (phoneErr) newErrors.contactPhone = phoneErr;
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const doSave = (sendInvitation: boolean) => {
    const payload: CreatePracticePayload = {
      name: form.name,
      vertical: form.vertical,
      specialty: form.specialty || undefined,
      website: form.website || undefined,
      phone: form.phone || undefined,
      address: form.address || undefined,
      city: form.city || undefined,
      state: form.state || undefined,
      zip: form.zip || undefined,
      contact: form.contact,
      sendInvitation,
    };
    mutate(payload, { onSuccess: handleClose });
  };

  const handleSave = async (sendInvitation: boolean) => {
    if (!validate()) return;
    if (sendInvitation) {
      setChecking(true);
      try {
        const result = await agencyApi.checkInviteConflict(form.contact.email);
        if (result.hasConflict) {
          setConflict({ existingPractices: result.existingPractices });
          setChecking(false);
          return;
        }
      } catch {
        // If check fails, proceed anyway
      }
      setChecking(false);
    }
    doSave(sendInvitation);
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Add New Client" size="lg">
      <div className={styles.form}>
        <div className={styles.section}>
          <h4 className={styles.sectionTitle}>Practice Details</h4>
          <Input
            label="Practice Name"
            value={form.name}
            onChange={(e) => {
              setForm((p) => ({ ...p, name: e.target.value }));
              setErrors((p) => ({ ...p, name: '' }));
            }}
            error={errors.name}
            fullWidth
          />
          <div className={styles.grid2}>
            <Select
              label="Vertical"
              value={form.vertical}
              // Changing vertical invalidates the chosen specialty — the option
              // lists do not overlap, so keeping it would persist a value that
              // belongs to another industry.
              onChange={(e) =>
                setForm((p) => ({ ...p, vertical: e.target.value as VerticalId, specialty: '' }))
              }
              options={VERTICAL_OPTIONS}
              fullWidth
            />
            <Select
              label="Specialty"
              value={form.specialty}
              onChange={(e) => setForm((p) => ({ ...p, specialty: e.target.value }))}
              options={specialtyOptions(form.vertical)}
              fullWidth
            />
          </div>
          <div className={styles.grid2}>
            <Input
              label="Website"
              value={form.website}
              onChange={(e) => setForm((p) => ({ ...p, website: e.target.value }))}
              placeholder="https://example.com"
              fullWidth
            />
            <Input
              label="Phone"
              type="tel"
              value={form.phone}
              onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
              onBlur={(e) => setForm((p) => ({ ...p, phone: formatPhoneDisplay(e.target.value) }))}
              placeholder={PHONE_PLACEHOLDER}
              fullWidth
            />
          </div>
          <Input
            label="Address"
            value={form.address}
            onChange={(e) => setForm((p) => ({ ...p, address: e.target.value }))}
            fullWidth
          />
          <div className={styles.grid3}>
            <Input
              label="City"
              value={form.city}
              onChange={(e) => setForm((p) => ({ ...p, city: e.target.value }))}
              fullWidth
            />
            <Input
              label="State"
              value={form.state}
              onChange={(e) => setForm((p) => ({ ...p, state: e.target.value }))}
              fullWidth
            />
            <Input
              label="ZIP"
              value={form.zip}
              onChange={(e) => setForm((p) => ({ ...p, zip: e.target.value }))}
              fullWidth
            />
          </div>
        </div>

        <div className={styles.section}>
          <h4 className={styles.sectionTitle}>Primary Contact</h4>
          <p className={styles.sectionNote}>An invitation will be sent to this person.</p>
          <div className={styles.grid2}>
            <Input
              label="Contact Name"
              value={form.contact.name}
              onChange={(e) => setContact('name', e.target.value)}
              error={errors.contactName}
              fullWidth
            />
            <Input
              label="Phone"
              type="tel"
              value={form.contact.phone}
              onChange={(e) => setContact('phone', e.target.value)}
              onBlur={(e) => setContact('phone', formatPhoneDisplay(e.target.value))}
              placeholder={PHONE_PLACEHOLDER}
              error={errors.contactPhone}
              fullWidth
            />
          </div>
          <Input
            label="Email"
            type="email"
            value={form.contact.email}
            onChange={(e) => setContact('email', e.target.value)}
            placeholder="contact@practice.com"
            error={errors.email}
            fullWidth
          />
        </div>

        {conflict && (
          <Callout variant="warning" title="User already belongs to another practice">
            <strong>{form.contact.email}</strong> is already a member of{' '}
            {conflict.existingPractices.length} other{' '}
            {conflict.existingPractices.length === 1 ? 'practice' : 'practices'} &mdash;{' '}
            {conflict.existingPractices.map((p, i, arr) => (
              <span key={p.id}>
                {i > 0 && (i === arr.length - 1 ? ' and ' : ', ')}
                {p.name}
              </span>
            ))}
            . Sending this invitation will allow them to join this practice as well.
            <div className={styles.conflictActions}>
              <Button variant="ghost" size="sm" onClick={() => setConflict(null)}>
                Cancel
              </Button>
              <Button
                size="sm"
                loading={isPending}
                onClick={() => {
                  setConflict(null);
                  doSave(true);
                }}
              >
                Send Anyway
              </Button>
            </div>
          </Callout>
        )}

        <div className={styles.actions}>
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="secondary" onClick={() => handleSave(false)} loading={isPending}>
            Save as Draft
          </Button>
          <Button onClick={() => handleSave(true)} loading={isPending || checking}>
            Save &amp; Send Invite
          </Button>
        </div>
      </div>
    </Modal>
  );
}
